import { PageHeader, PageShell } from '@/components/PageShell';
import { TabNav } from '@/components/TabNav';
import {
    allEquipmentTypesState,
    recallCountsState,
    recallsAwaitingTopicApprovalCountState,
    recallsStateDocState,
    recallTopicsState,
    recentRecallJobsState,
    stuckRecallCandidatesState,
} from '@/state';
import { useAsyncAtomValue, useFreshOnMountAtomValue, useLoadableAtomValue } from '@/state/loadableHooks';
import { formatRelative } from '@/util/dates';
import { RECALL_CLASSIFY_VERSION, RECALL_RULES_VERSION, recallCatalogVersion } from '@rock/recalls';
import type { RecallsState } from '@rock/types';
import type { FC } from 'react';
import { NoMatchedTypeTab } from './NoMatchedTypeTab';
import { CodeTag } from './recallTags';
import { RecallsCatalogTab } from './RecallsCatalogTab';
import { describeActiveRecallJob, isRecallJobActive } from './recallJobs';
import { catalogRecallCount, stalledRecalls, untypedRecallCount } from './recallStatus';
import { RecallTopicsTab } from './RecallTopicsTab';
import { RejectedRecallsTab } from './RejectedRecallsTab';
import { StalledRecallsTab } from './StalledRecallsTab';

type RecallsTab = 'catalog' | 'no-matched-type' | 'topics' | 'rejected' | 'stalled';

/**
 * CPSC recalls, in tabs: the Catalog (drafts and approved, under review filters), No Matched Type (the
 * untyped recalls by product kind), Topics (the FYI topics and the recalls awaiting approval under each),
 * Rejected, and Stalled while any recall is stalled short of review. Opposite the title, the whole pipeline's
 * state: the last sync, the versions it runs, and any job still running whichever tab started it.
 */
export const RecallsPage: FC<{ tab: RecallsTab }> = ({ tab }) => {
    const { value: counts } = useFreshOnMountAtomValue(recallCountsState);
    const { value: awaitingTopicApproval } = useFreshOnMountAtomValue(recallsAwaitingTopicApprovalCountState);
    const state = useAsyncAtomValue(recallsStateDocState);
    const [candidates, candidatesLoadable] = useLoadableAtomValue(stuckRecallCandidatesState);
    const stalled = candidates && stalledRecalls(candidates);
    const showStalledTab = tab === 'stalled' || !!stalled?.length;

    return (
        <PageShell>
            <PageHeader
                title="Recalls"
                description="CPSC recalls matched against the equipment catalog. Nothing reaches a homeowner until a recall is approved."
            >
                <PipelineStatus state={state} />
            </PageHeader>
            <TabNav
                tabs={[
                    { label: 'Catalog', to: '/products/recalls/catalog', count: catalogRecallCount(counts) },
                    {
                        label: 'No Matched Type',
                        to: '/products/recalls/no-matched-type',
                        count: untypedRecallCount(state),
                    },
                    { label: 'Topics', to: '/products/recalls/topics', count: awaitingTopicApproval },
                    { label: 'Rejected', to: '/products/recalls/rejected', count: counts?.byStatus.rejected },
                    ...(showStalledTab
                        ? [{ label: 'Stalled', to: '/products/recalls/stalled', count: stalled?.length }]
                        : []),
                ]}
            />
            {tab === 'catalog' && <RecallsCatalogTab counts={counts} />}
            {tab === 'no-matched-type' && <NoMatchedTypeTab state={state} />}
            {tab === 'topics' && <RecallTopicsTab />}
            {tab === 'rejected' && <RejectedRecallsTab />}
            {tab === 'stalled' && (
                <StalledRecallsTab stalled={stalled} failed={candidatesLoadable.state === 'hasError'} />
            )}
        </PageShell>
    );
};

/** The pipeline at a glance: the last sync, the versions it runs, and any job still running. */
const PipelineStatus: FC<{ state: RecallsState | undefined }> = ({ state }) => {
    const jobs = useAsyncAtomValue(recentRecallJobsState, []);
    const types = useAsyncAtomValue(allEquipmentTypesState);
    const topics = useAsyncAtomValue(recallTopicsState);
    const activeJobs = jobs.filter(isRecallJobActive);

    return (
        <div className="flex flex-col items-end gap-1.5 text-xs text-muted-foreground">
            <span>
                <span className="font-semibold">Last sync:</span> {describeSync(state)}
            </span>
            <span className="flex flex-wrap justify-end gap-1">
                <CodeTag>{RECALL_CLASSIFY_VERSION}</CodeTag>
                <CodeTag>{RECALL_RULES_VERSION}</CodeTag>
                <CodeTag>catalog {types && topics ? recallCatalogVersion(types, topics) : '…'}</CodeTag>
            </span>
            {activeJobs.map((job) => (
                <span key={job.id} className="text-amber-700">
                    {describeActiveRecallJob(job)}
                </span>
            ))}
        </div>
    );
};

function describeSync(state: RecallsState | undefined): string {
    if (!state?.lastSyncAt) return 'never';
    const outcome = state.lastSyncOutcome && state.lastSyncOutcome !== 'ok' ? ` · ${state.lastSyncOutcome}` : '';
    return `${formatRelative(state.lastSyncAt)} · ${state.lastSyncCount ?? '?'} fetched${outcome}`;
}
